import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Receipt, User, Mail, FileText, Banknote, FileCheck, Download, AlertCircle, CheckCircle2, Clock, CalendarDays, Building2, Hash, Upload, Send, ThumbsUp, RotateCcw, MessageSquare, Circle, ExternalLink, FolderUp, Eye, EyeOff, UserCog, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { SubmissionLayout, DetailSection, DetailField, DownloadActions, StatusBadge, DeleteSubmissionButton } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import SmartClientMatterSearch from "../../components/SmartClientMatterSearch";
import StaffPicker from "../../components/StaffPicker";
import NativeInvoiceDraft from "../../components/NativeInvoiceDraft";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { getUserRole, ROLES } from "../../config/roleConfig";
import { ALLOCATE_FE_EXTRA_EMAILS } from "../../config/staffConfig";
import { INVOICE_REQUEST_FEES, LEGACY_INVOICE_REQUEST_FEES, feeRate } from "../../lib/invoiceRequestFees";

function AlertBanner({ icon: Icon, color, children }) {
  const styles = {
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`p-3 rounded-md border flex items-center gap-2.5 text-sm ${styles[color]}`} data-testid={`alert-${color}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <span className="font-medium">{children}</span>
    </div>
  );
}

function HeroField({ icon: Icon, label, value, large = false }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/10 last:border-0">
      <div className="w-8 h-8 rounded-md bg-teal/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-teal" />
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider block">{label}</span>
        <span className={`font-medium text-white block truncate ${large ? 'text-base' : 'text-sm'}`}>
          {value || <span className="text-slate-500 font-normal">—</span>}
        </span>
      </div>
    </div>
  );
}

function InvoiceTypeBadge({ type }) {
  if (!type) return null;
  const isInterim = type?.toLowerCase() === "interim";
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide ${
      isInterim
        ? "bg-status-amber-dim text-status-amber border border-amber-200"
        : "bg-teal-dim text-teal border border-teal"
    }`}>
      {type}
    </span>
  );
}

function FinancialRow({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-navy-border/30 last:border-0">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-teal' : 'text-white'}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function TimelineStep({ label, done, active }) {
  return (
    <div className="flex items-center gap-2">
      {done ? (
        <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />
      ) : active ? (
        <Clock className="w-5 h-5 text-status-amber shrink-0 animate-pulse" />
      ) : (
        <Circle className="w-5 h-5 text-slate-500 shrink-0" />
      )}
      <span className={`text-sm ${done ? "text-teal font-medium" : active ? "text-status-amber" : "text-slate-500"}`}>
        {label}
      </span>
    </div>
  );
}

// Persisted preview-open state — keyed per Vanessa's spec so a reviewer
// who left preview expanded keeps it expanded on the next submission they
// open. Default is COLLAPSED (slow-connection-safe).
const DRAFT_PREVIEW_LS_KEY = "invoiceTracker.draftPreview.open";

function _readPreviewOpenLS() {
  try {
    return window.localStorage.getItem(DRAFT_PREVIEW_LS_KEY) === "1";
  } catch {
    return false;
  }
}

function _writePreviewOpenLS(open) {
  try {
    window.localStorage.setItem(DRAFT_PREVIEW_LS_KEY, open ? "1" : "0");
  } catch {
    /* localStorage may be unavailable (private mode, quota); preview still works in-memory */
  }
}

function _detectMimeFromFilename(filename) {
  if (!filename) return "";
  const ext = filename.toLowerCase().split(".").pop();
  if (ext === "pdf") return "application/pdf";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  if (ext === "docx") return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (ext === "xlsx") return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  return "";
}

// Inline preview of the uploaded invoice draft. The base64 payload is
// returned by GET /submissions/{id} as part of invoice_tracker.draft_file_data
// — the SAME authenticated path the rest of the panel already uses. If the
// fee earner can't see the submission, they can't see the preview, by
// construction (no separate endpoint to mis-authz).
function DraftPreview({ filename, base64Data, downloadUrl, onDownload }) {
  const [open, setOpen] = useState(() => _readPreviewOpenLS());
  const [loadError, setLoadError] = useState("");

  const mime = useMemo(() => _detectMimeFromFilename(filename), [filename]);
  const isPdf = mime === "application/pdf";
  const isImage = mime.startsWith("image/");
  const isSupported = isPdf || isImage;

  const dataUrl = useMemo(() => {
    if (!base64Data || !mime) return "";
    return `data:${mime};base64,${base64Data}`;
  }, [base64Data, mime]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      _writePreviewOpenLS(next);
      return next;
    });
  }, []);

  const handleLoadError = useCallback((err) => {
    // Loud failure (Vanessa's rule): never blank silently. If iframe/img
    // can't render the embedded blob, surface a real message via the
    // standard window.errorMessage(err, fallback) channel.
    setLoadError(errorMessage(err, "Could not load preview — try downloading the file instead."));
  }, []);

  // If there's a download URL (legacy attachments) but no inline base64,
  // we can still iframe the URL for PDFs. Prefer base64 when present
  // (zero extra round-trip).
  const previewSrc = dataUrl || downloadUrl || "";

  return (
    <div className="mt-2" data-testid="draft-preview-container">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleToggle}
          disabled={!isSupported && !downloadUrl}
          className="text-xs text-teal hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
          data-testid="draft-preview-toggle"
          aria-expanded={open}
        >
          {open ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {open ? "Hide preview" : "Show preview"}
        </button>
        {onDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="text-xs text-teal hover:underline inline-flex items-center gap-1"
            data-testid="draft-preview-download"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2" data-testid="draft-preview-body">
          {!isSupported ? (
            <div
              className="p-3 rounded-md border border-white/10 bg-[color:var(--surface-inset)] text-sm text-slate-600"
              data-testid="draft-preview-unsupported"
            >
              Preview not available for this file type.
              {onDownload && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={onDownload}
                    className="text-teal hover:underline"
                    data-testid="draft-preview-unsupported-download"
                  >
                    Click to download
                  </button>
                  .
                </>
              )}
            </div>
          ) : loadError ? (
            <div
              className="p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2"
              data-testid="draft-preview-error"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{loadError}</span>
            </div>
          ) : !previewSrc ? (
            <div
              className="p-3 rounded-md border border-amber-200 bg-status-amber-dim text-sm text-status-amber flex items-start gap-2"
              data-testid="draft-preview-missing"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Preview data is missing. Try reuploading the draft.</span>
            </div>
          ) : isPdf ? (
            <iframe
              src={previewSrc}
              title={`Draft preview — ${filename}`}
              className="w-full h-[500px] rounded-md border border-white/10 bg-white"
              data-testid="draft-preview-iframe"
              onError={handleLoadError}
            />
          ) : (
            <img
              src={previewSrc}
              alt={`Draft preview — ${filename}`}
              className="max-w-full max-h-[500px] rounded-md border border-white/10 bg-white"
              data-testid="draft-preview-image"
              onError={(e) => handleLoadError(e?.nativeEvent || new Error("Image failed to load"))}
            />
          )}
        </div>
      )}
    </div>
  );
}

function InvoiceTrackerPanel({ submission }) {
  const tracker = submission?.invoice_tracker || {};
  const submissionId = submission?.id;
  const currentUserEmail = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  const currentUserName = sessionStorage.getItem("bonsai_user_name") || localStorage.getItem("bonsai_user_name") || "";
  const currentUserRole = getUserRole(currentUserEmail);
  const isMay = currentUserRole === ROLES.ACCOUNTS || currentUserRole === ROLES.DIRECTOR;

  const [draftUploaded, setDraftUploaded] = useState(tracker.draft_uploaded || false);
  const [draftFilename, setDraftFilename] = useState(tracker.draft_filename || "");
  const [draftFileData, setDraftFileData] = useState(tracker.draft_file_data || "");
  const [draftUploadedAt, setDraftUploadedAt] = useState(tracker.draft_uploaded_at || "");
  // time-recording-foundations invoice engine — the client_invoices id
  // once a native draft has been generated, so the "issue" stage reuses
  // the SAME record (with any edits) instead of regenerating a fresh one.
  const [nativeInvoiceId, setNativeInvoiceId] = useState(tracker.native_invoice_id || "");
  const [notifiedAt, setNotifiedAt] = useState(tracker.fee_earner_notified_at || "");
  const [notifiedBy, setNotifiedBy] = useState(tracker.fee_earner_notified_by || "");
  const [approvalStatus, setApprovalStatus] = useState(tracker.approval_status || "");
  const [approvedBy, setApprovedBy] = useState(tracker.approved_by || "");
  const [approvedAt, setApprovedAt] = useState(tracker.approved_at || "");
  const [annotation, setAnnotation] = useState(tracker.annotation || "");
  const [scratchpad, setScratchpad] = useState(tracker.scratchpad || "");
  const [scratchpadMeta, setScratchpadMeta] = useState({
    by: tracker.scratchpad_updated_by || "",
    at: tracker.scratchpad_updated_at || "",
  });
  const [uploading, setUploading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);

  // Brief 31 — cancellation modal state. Mandatory reason captured here;
  // server enforces non-empty + <=1000 char on PATCH.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Cancellation metadata hydrated from the tracker subdoc on load.
  const [cancellationReason, setCancellationReason] = useState(tracker.cancellation_reason || "");
  const [cancelledAt, setCancelledAt] = useState(tracker.cancelled_at || "");
  const [cancelledBy, setCancelledBy] = useState(tracker.cancelled_by || "");
  const [supersedingInvoice, setSupersedingInvoice] = useState(tracker.superseding_invoice || null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const scratchpadTimerRef = useRef(null);

  // feat/allocate-fe-on-invoice-request (19 May 2026):
  // When notify-fee-earner returns 409 ("no fee earner assigned"), May
  // and any director currently have to navigate to the matter intake
  // screen to fix it. That's a real cashflow blocker — invoice #11070
  // on BL.VEC.006.0031 sat unsent for >24h for exactly this reason.
  // This dialog calls the SAME backend endpoint (PR #477/#478):
  //   POST /api/matters/{matter_ref}/allocate-fee-earner
  // which is gated to directors + cashier server-side, so we mirror
  // that gate in the UI (isMay covers both ACCOUNTS and DIRECTOR).
  const [allocFeOpen, setAllocFeOpen] = useState(false);
  const [allocFeOptions, setAllocFeOptions] = useState([]);
  const [allocFeLoading, setAllocFeLoading] = useState(false);
  const [allocFeSelected, setAllocFeSelected] = useState("");
  const [allocFeSubmitting, setAllocFeSubmitting] = useState(false);

  // Phase 1 finance — reassign the allocated fee earner from the detail
  // view. Unlike the isMay-gated allocate dialog above (which locks once a
  // FE is set), reassignment is available to an MD, the current fee earner,
  // or the original requester, and calls the dedicated
  // PATCH /submissions/{id}/reassign-fe endpoint that enforces the same gate.
  const [reassignFeSelected, setReassignFeSelected] = useState("");
  const [reassigningFe, setReassigningFe] = useState(false);

  // Email client state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailBody, setEmailBody] = useState("");
  // Sent-to-client manual flip state (23 May 2026, fix/invoice-tracker-may-bleed):
  // Open in Outlook is a one-way handoff — nothing tells CaseMinder the
  // invoice actually went out. May reported the tracker stayed stuck on
  // "Approved" forever. This state hydrates from the tracker so the
  // button hides for already-sent rows and the badge can render the
  // timestamp the moment the PATCH lands.
  const [sentToClientAt, setSentToClientAt] = useState(tracker.sent_to_client_at || "");

  // Final invoice upload state
  const [finalInvoiceFilename, setFinalInvoiceFilename] = useState(tracker.final_invoice_filename || "");
  const [finalInvoiceUploadedAt, setFinalInvoiceUploadedAt] = useState(tracker.final_invoice_uploaded_at || "");
  // Brief 9 Phase B: keep the data-URL of the freshly-uploaded finalised
  // invoice in component state so handleFileToSharePoint can convert it
  // back to a Blob and POST it as multipart `file` to /bonsai/filing/upload.
  // The base64 was already sent to the backend via final_invoice_data on
  // upload — this is a session-scoped echo (cleared on refresh; user
  // re-uploads is acceptable for V1).
  const [finalInvoiceDataUrl, setFinalInvoiceDataUrl] = useState("");
  const [uploadingFinal, setUploadingFinal] = useState(false);
  const finalFileInputRef = useRef(null);

  // SharePoint filing state — Brief 9 Phase B
  // spMatterPick replaces the bare matter-ref string with the structured
  // payload returned by <SmartClientMatterSearch />: { matter_reference,
  // matter_title, client_name }. Save-to-SP button is enabled when
  // matter_reference is non-empty.
  const [spMatterPick, setSpMatterPick] = useState({ matter_reference: "", matter_title: "", client_name: "" });
  const [filingToSp, setFilingToSp] = useState(false);
  const [showSpFiling, setShowSpFiling] = useState(false);

  // Brief 32 — subfolder picker state cluster. Previously the FE
  // hardcoded subfolder="07 Costs" (firm folder convention uses
  // Financials / Correspondence / Documents, not numbered prefixes),
  // so the resolver missed every time and the upload silently fell back
  // to matter root. The picker fetches the real subfolder list from the
  // new GET /bonsai/filing/matter-subfolders endpoint when the modal
  // opens and pre-selects Financials when the backend returns it as the
  // default. Silent matter-root fallback on a resolution miss is
  // forbidden — see hard-fail in handleFileToSharePoint below.
  const [spSubfolders, setSpSubfolders] = useState([]);
  const [spSubfolderLoading, setSpSubfolderLoading] = useState(false);
  const [spSubfolderError, setSpSubfolderError] = useState("");
  const [spSelectedSubfolder, setSpSelectedSubfolder] = useState("");
  const [spDefaultSubfolder, setSpDefaultSubfolder] = useState(null);

  // Brief 32 — fetch the real subfolder list when the modal opens
  // against a non-empty matter reference. cancelledLocal flag guards
  // against late-arriving responses overwriting picker state if the
  // user closed the modal mid-flight. 404 / 503 map to friendly copy;
  // everything else falls through to safeErrorMessage at call time.
  useEffect(() => {
    const ref = (spMatterPick.matter_reference || "").trim();
    if (!showSpFiling || !ref) return undefined;

    let cancelledLocal = false;
    setSpSubfolderLoading(true);
    setSpSubfolderError("");
    setSpSubfolders([]);
    setSpSelectedSubfolder("");
    setSpDefaultSubfolder(null);

    axios
      .get(`${API}/bonsai/filing/matter-subfolders`, {
        params: { matter_reference: ref },
        withCredentials: true,
      })
      .then((resp) => {
        if (cancelledLocal) return;
        const data = resp.data || {};
        const folders = Array.isArray(data.subfolders) ? data.subfolders : [];
        setSpSubfolders(folders);
        const dflt = data.default_subfolder || "";
        setSpDefaultSubfolder(dflt || null);
        setSpSelectedSubfolder(dflt || "");
      })
      .catch((err) => {
        if (cancelledLocal) return;
        // nosilent: surface a real reason; never an empty picker with no
        // explanation. 404 / 503 carry friendly copy; everything else
        // falls through to err.message / detail string.
        const status = err?.response?.status;
        if (status === 404) {
          setSpSubfolderError(`Matter folder not found for ${ref}`);
        } else if (status === 503) {
          setSpSubfolderError(
            "SharePoint is not configured on the server"
          );
        } else {
          setSpSubfolderError(errorMessage(err, "Failed to list subfolders"));
        }
        // eslint-disable-next-line no-console
        console.error("[INVOICE_SP_SUBFOLDERS]", err);
      })
      .finally(() => {
        if (!cancelledLocal) setSpSubfolderLoading(false);
      });

    return () => {
      cancelledLocal = true;
    };
  }, [showSpFiling, spMatterPick.matter_reference]);

  // Submission data for email prefill
  const subData = submission?.data || {};
  const invoiceEmail = subData.client_invoice_email || subData.invoice_email || subData.Q_email || "";
  const clientName = subData.client_name || submission?.primary_label || "";
  const matterReference = subData.matter_reference || submission?.secondary_label || "";

  // feat/invoice-tracker-button-state (20 May 2026):
  // Vanessa's screenshot showed the Allocate and Notify buttons still
  // fully active even when the matter already had a fee earner AND a
  // notification had been sent. Both states drive UI affordances below.
  // Read order mirrors _resolve_matter_fee_earner_email on the backend
  // so the UI matches what the notify endpoint will actually pick.
  const allocatedFeeEarner = (
    subData.responsible_fee_earner
    || subData.Q6
    || subData.fee_earner_name
    || subData.fee_earner
    || submission?.responsible_fee_earner
    || ""
  ).toString().trim();
  const feeEarnerAllocated = Boolean(allocatedFeeEarner);
  const alreadyNotified = Boolean(notifiedAt);
  // BM-H4554: a returned draft is sent again once May has revised it. The
  // return used to be the end of the road: Send stayed locked and the
  // screen offered no Approve, so a revised draft could never be approved.
  const isReturned = approvalStatus === "returned";
  const sendLocked = alreadyNotified && !isReturned;

  const amberAccent = isMay;

  // Phase 1 finance — who may reassign the fee earner. Mirrors the backend
  // gate on PATCH /submissions/{id}/reassign-fe: an MD/director, the
  // currently-allocated fee earner, or the original requester.
  //
  // BM-H126 (29 Jul 2026): also include May (cashier) and Liezel (exec
  // assistant). Same principle as the matter-detail Allocate Fee
  // Earner button — the people who clean up finance mis-routes should
  // not have to escalate to a director.
  const currentUserEmailLc = (currentUserEmail || "").toLowerCase().trim();
  const feeEarnerEmailLc = (subData.fee_earner_email || "").toLowerCase().trim();
  const requesterEmailLc = (
    subData.submitter_email || subData.requester_email || submission?.requester_email || ""
  ).toLowerCase().trim();
  const canReassignFe = Boolean(
    currentUserEmailLc && (
      currentUserRole === ROLES.DIRECTOR
      || (feeEarnerEmailLc && currentUserEmailLc === feeEarnerEmailLc)
      || (requesterEmailLc && currentUserEmailLc === requesterEmailLc)
      || ALLOCATE_FE_EXTRA_EMAILS.includes(currentUserEmailLc)
    )
  );

  // Lift the real backend error to the toast / log instead of showing a
  // generic "Failed to update tracker" — that was the false-success
  // shape that hid LEG A of the 6 May 2026 P Dot bug. ``throwOnError``
  // lets the upload path distinguish between a user-visible patch
  // failure and a normal partial update (e.g. scratchpad blur).
  const updateTracker = useCallback(async (fields, { throwOnError = false } = {}) => {
    try {
      const resp = await axios.patch(`${API}/submissions/${submissionId}/invoice-tracker`, {
        ...fields,
        updated_by: currentUserName || currentUserEmail,
      });
      return resp.data;
    } catch (err) {
      const msg = errorMessage(err, "Failed to update tracker");
      if (throwOnError) {
        throw new Error(msg);
      }
      toast.error(msg);
      return null;
    }
  }, [submissionId, currentUserName, currentUserEmail]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF and DOCX files are accepted");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setUploading(false);
      toast.error("Could not read the file. Try again.");
    };
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const result = await updateTracker(
          {
            draft_uploaded: true,
            draft_filename: file.name,
            draft_file_data: base64,
          },
          { throwOnError: true },
        );
        if (!result) {
          // updateTracker returns null only on the non-throw path, which
          // we did not request here — this branch is a defensive no-op.
          throw new Error("Upload returned no response");
        }
        setDraftUploaded(true);
        setDraftFilename(file.name);
        setDraftFileData(base64);
        setDraftUploadedAt(result.draft_uploaded_at || new Date().toISOString());
        // BM-H4554: saving a draft no longer emails the fee earner.
        toast.success("Draft saved. Press Send to fee earner when it is ready.");
      } catch (err) {
        toast.error(errorMessage(err, "Upload failed"));
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  }, [updateTracker, currentUserName, currentUserEmail]);

  const handleDownloadDraft = useCallback(() => {
    if (!draftFileData || !draftFilename) {
      toast.error("Draft file is not available for download");
      return;
    }
    try {
      const mime = _detectMimeFromFilename(draftFilename) || "application/octet-stream";
      const byteChars = atob(draftFileData);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = draftFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, "Could not download draft — try replacing the file."));
    }
  }, [draftFileData, draftFilename]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // feat/allocate-fe-on-invoice-request — open dialog + fetch FE list.
  // Mirrors MatterIntakeDetail.handleOpenAllocateFeeEarner exactly so
  // both code paths stay in lock-step. Defined BEFORE handleNotifyFeeEarner
  // so the 409 fallback path inside Notify can reference it.
  const handleOpenAllocateFeeEarner = useCallback(async () => {
    setAllocFeSelected("");
    setAllocFeOpen(true);
    if (allocFeOptions.length === 0) {
      setAllocFeLoading(true);
      try {
        const res = await axios.get(`${API}/lookup/fee-earners`);
        const opts = Array.isArray(res?.data) ? res.data : [];
        setAllocFeOptions(opts);
      } catch (err) {
        toast.error(errorMessage(err, "Failed to load fee earner list"));
        setAllocFeOpen(false);
      } finally {
        setAllocFeLoading(false);
      }
    }
  }, [allocFeOptions.length]);

  // feat/allocate-fe-on-invoice-request — POST to the existing matters
  // endpoint. On success, refetch the page so the matter banner /
  // pipeline / submission record all reflect the new fee earner. We
  // don't try to patch the submission inline because the FE name is
  // resolved server-side from staff_config and can affect routing
  // through several downstream pieces (notifications, audit, etc).
  const handleSubmitAllocateFeeEarner = useCallback(async () => {
    const name = (allocFeSelected || "").trim();
    if (!name) {
      toast.error("Pick a fee earner from the list");
      return;
    }
    if (!matterReference) {
      toast.error("Matter reference not found on this submission");
      return;
    }
    setAllocFeSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterReference)}/allocate-fee-earner`,
        { fee_earner_name: name },
      );
      if (res?.data?.success) {
        const newFE = res.data.fee_earner?.name || name;
        const prev = res.data.previous_fee_earner || "";
        if (res.data.no_change) {
          toast.info(`Fee earner unchanged (already ${newFE})`);
        } else {
          toast.success(
            prev
              ? `Fee earner reallocated: ${prev} → ${newFE}`
              : `Fee earner allocated: ${newFE}`,
          );
        }
        setAllocFeOpen(false);
        // Reload so the submission re-fetches with the new FE and
        // "Notify Fee Earner" can now succeed. window.location.reload
        // is intentional rather than a local re-fetch: this screen has
        // many derived caches (pipeline, banner) that would otherwise
        // need separate cache busts.
        if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
          window.location.reload();
        }
      } else {
        toast.error("Allocate Fee Earner returned an unexpected response");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Allocate Fee Earner failed"));
    } finally {
      setAllocFeSubmitting(false);
    }
  }, [allocFeSelected, matterReference]);

  // Phase 1 finance — reassign the allocated fee earner via the dedicated
  // endpoint. Reload on success (mirrors the allocate path) so every derived
  // cache on this screen reflects the new FE.
  const handleReassignFeeEarner = useCallback(async () => {
    const name = (reassignFeSelected || "").trim();
    if (!name) {
      toast.error("Pick a fee earner from the list");
      return;
    }
    setReassigningFe(true);
    try {
      const res = await axios.patch(
        `${API}/submissions/${submissionId}/reassign-fe`,
        { new_fe: name },
      );
      if (res?.data?.success) {
        const newFE = res.data.fee_earner?.name || name;
        const prev = res.data.previous_fee_earner || "";
        toast.success(
          prev ? `Fee earner reassigned: ${prev} → ${newFE}` : `Fee earner assigned: ${newFE}`,
        );
        if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
          window.location.reload();
        }
      } else {
        toast.error("Reassign Fee Earner returned an unexpected response");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Reassign Fee Earner failed"));
    } finally {
      setReassigningFe(false);
    }
  }, [reassignFeSelected, submissionId]);

  const handleNotifyFeeEarner = useCallback(async () => {
    setNotifying(true);
    try {
      const resp = await axios.post(`${API}/submissions/${submissionId}/notify-fee-earner`, {
        notified_by: currentUserName || currentUserEmail,
      });
      if (resp.data.success) {
        setNotifiedAt(resp.data.notified_at);
        setNotifiedBy(currentUserName || currentUserEmail);
        // 8-9 May 2026 regression (BL.VEC.006.0051): the manual notify
        // path now advances approval_status server-side. Mirror that
        // in local state so the pill moves immediately, not only after
        // a full reload.
        if (resp.data.approval_status) {
          setApprovalStatus(resp.data.approval_status);
        }
        toast.success(`Notification sent to ${resp.data.notified_email}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      // 409 means the matter has no fee earner assigned — actionable for May.
      // feat/allocate-fe-on-invoice-request (19 May 2026): on 409, auto-open
      // the Allocate dialog so the operator can self-serve instead of
      // bouncing to the matter intake page. We still surface the toast
      // (no silent swallowing) — the dialog is purely an accelerator.
      if (status === 409) {
        toast.error(errorMessage(err, "No fee earner assigned to this matter."));
        if (isMay && matterReference) {
          // Pre-open the dialog. Loading the FE list is idempotent.
          handleOpenAllocateFeeEarner();
        }
      } else {
        toast.error(errorMessage(err, "Failed to send notification"));
      }
    } finally {
      setNotifying(false);
    }
  }, [submissionId, currentUserName, currentUserEmail, isMay, matterReference, handleOpenAllocateFeeEarner]);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    const result = await updateTracker({ approval_status: "approved" });
    if (result) {
      setApprovalStatus("approved");
      setApprovedBy(currentUserName || currentUserEmail);
      setApprovedAt(result.approved_at || new Date().toISOString());
      toast.success("Invoice approved");
    }
    setApproving(false);
  }, [updateTracker, currentUserName, currentUserEmail]);

  const handleReturn = useCallback(async () => {
    if (!annotation.trim()) {
      toast.error("Please add annotations before returning");
      return;
    }
    setApproving(true);
    const result = await updateTracker({ approval_status: "returned", annotation: annotation.trim() });
    if (result) {
      setApprovalStatus("returned");
      setApprovedBy(currentUserName || currentUserEmail);
      setApprovedAt(result.approved_at || new Date().toISOString());
      toast.success("Invoice returned for revision");
      setShowAnnotation(false);
    }
    setApproving(false);
  }, [updateTracker, annotation, currentUserName, currentUserEmail]);

  // Brief 31 — cancellation handler. Mandatory reason (50-1000 char on
  // the FE; server enforces non-empty + <=1000). Never claims success
  // on a failed PATCH — updateTracker returns null on error and the
  // success branch is gated on a truthy result.
  const isCancelled = approvalStatus === "cancelled";
  // Brief 48 (22 Jun 2026) - removed the 50-character minimum on the
  // cancellation reason: the previous gate left the Cancel Draft button
  // dead with no visible explanation when a perfectly clear short reason
  // ('duplicate of inv 4521') was typed. Backend already accepts any
  // non-blank reason up to 1000 chars; the FE now matches.
  const handleSubmitCancel = useCallback(async () => {
    const reason = (cancelReason || "").trim();
    if (!reason) {
      toast.error("Please give a reason before cancelling the draft");
      return;
    }
    if (reason.length > 1000) {
      toast.error("Cancellation reason must be at most 1000 characters");
      return;
    }
    setCancelling(true);
    const result = await updateTracker({
      approval_status: "cancelled",
      cancellation_reason: reason,
    });
    if (result) {
      setApprovalStatus("cancelled");
      setCancellationReason(result.cancellation_reason || reason);
      setCancelledAt(result.cancelled_at || new Date().toISOString());
      setCancelledBy(result.cancelled_by || currentUserName || currentUserEmail || "");
      // superseding_invoice is computed server-side on the next GET; the
      // PATCH response does not include it. Leaving the existing value
      // null means the banner shows "No superseding invoice on record"
      // until a re-load or a subsequent invoice is approved.
      toast.success("Draft cancelled");
      setShowCancelModal(false);
      setCancelReason("");
    }
    setCancelling(false);
  }, [updateTracker, cancelReason, currentUserName, currentUserEmail]);

  const handleScratchpadBlur = useCallback(() => {
    if (scratchpadTimerRef.current) clearTimeout(scratchpadTimerRef.current);
    scratchpadTimerRef.current = setTimeout(async () => {
      const result = await updateTracker({ scratchpad });
      if (result) {
        setScratchpadMeta({
          by: result.scratchpad_updated_by || currentUserName || currentUserEmail,
          at: result.scratchpad_updated_at || new Date().toISOString(),
        });
      }
    }, 300);
  }, [updateTracker, scratchpad, currentUserName, currentUserEmail]);

  const handleOpenEmailClient = useCallback(() => {
    const to = encodeURIComponent(invoiceEmail);
    const subject = encodeURIComponent(`Invoice — ${clientName} — ${matterReference}`);
    const body = encodeURIComponent(emailBody);
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_self");
  }, [invoiceEmail, clientName, matterReference, emailBody]);

  const handleFinalInvoiceUpload = useCallback(async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are accepted for the finalised invoice");
      return;
    }
    setUploadingFinal(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        const result = await updateTracker({
          final_invoice_filename: file.name,
          final_invoice_data: base64,
        });
        if (result) {
          setFinalInvoiceFilename(file.name);
          setFinalInvoiceUploadedAt(result.final_invoice_uploaded_at || new Date().toISOString());
          // Brief 9 Phase B: stash the data-URL so Save-to-SharePoint can
          // re-materialise the PDF blob for /bonsai/filing/upload without
          // a server round-trip to fetch the freshly-uploaded artifact.
          setFinalInvoiceDataUrl(reader.result || "");
          toast.success("Finalised invoice uploaded");
        }
        setUploadingFinal(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadingFinal(false);
      toast.error("Upload failed");
    }
  }, [updateTracker]);

  // time-recording-foundations invoice engine — native draft generation.
  // Feeds the rendered PDF into the SAME draft_file_data slot a manual
  // upload already sets (Vanessa, 26 Sep 2026: use the working page,
  // don't build a parallel one) so notify-fee-earner / approve / return
  // below all work completely unchanged.
  const handleNativeDraftReady = useCallback(async ({ pdfBase64, filename, invoiceId }) => {
    try {
      const result = await updateTracker(
        { draft_uploaded: true, draft_filename: filename, draft_file_data: pdfBase64, native_invoice_id: invoiceId },
        { throwOnError: true },
      );
      if (!result) throw new Error("Draft attach returned no response");
      setDraftUploaded(true);
      setDraftFilename(filename);
      setDraftFileData(pdfBase64);
      setDraftUploadedAt(result.draft_uploaded_at || new Date().toISOString());
      setNativeInvoiceId(invoiceId);
      // BM-H4554: saving no longer emails the fee earner; Send does.
      toast.message("Press Send to fee earner when the draft is ready.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not attach the generated draft"));
    }
  }, [updateTracker]);

  // time-recording-foundations invoice engine — issuing the real invoice.
  // Feeds the PDF into the SAME final_invoice_data slot a manual upload
  // sets, then creates the credit-control record via the existing
  // BM-H127 linked_invoice_request_id mechanism (same one May's manual
  // "create from pending invoice request" path already uses) so this
  // matter's chase timers start the same way a manual entry's would.
  const handleNativeFinalReady = useCallback(async ({ pdfBase64, filename, invoiceNumber, creditControl }) => {
    try {
      const result = await updateTracker(
        {
          final_invoice_filename: filename,
          final_invoice_data: pdfBase64,
          native_invoice_number: invoiceNumber,
        },
        { throwOnError: true },
      );
      if (!result) throw new Error("Final invoice attach returned no response");
      setFinalInvoiceFilename(filename);
      setFinalInvoiceUploadedAt(result.final_invoice_uploaded_at || new Date().toISOString());
      setFinalInvoiceDataUrl(`data:application/pdf;base64,${pdfBase64}`);
      toast.success("Finalised invoice attached");

      // BM-H4554: the server opened the credit control record as part of
      // finalising (once per invoice). It used to be a second call from
      // here, lost on a reload and repeatable on a double press.
      if (creditControl?.created) {
        toast.success("Credit control record created");
      } else if (creditControl?.error || !creditControl) {
        toast.warning("Invoice finalised, but the credit control record was not created. Add it on Credit Control.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not attach the issued invoice"));
    }
  }, [updateTracker]);

  // safeErrorMessage — coerce any backend error shape into a plain string
  // for sonner. FastAPI 422 returns detail as an array of pydantic dicts
  // ({type, loc, msg, input}); passing that to toast.error renders it as
  // a React child and trips React error #31 → ErrorBoundary takeover (the
  // "Something went wrong" screen May reported, 23 May 2026). Never pass
  // anything other than a string to a toast.
  const safeErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail) && detail.length) {
      const parts = detail.map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") return d.msg || JSON.stringify(d);
        return String(d);
      });
      return parts.join("; ");
    }
    if (detail && typeof detail === "object") {
      try { return JSON.stringify(detail); } catch { /* fallthrough */ }
    }
    return errorMessage(err, fallback);
  };

  const handleFileToSharePoint = useCallback(async (fileData, filename) => {
    // Brief 9 Phase B — replaces 23 May 2026 Phase A BAND-AID
    // (toast about the feature being temporarily disabled). Two-step flow:
    //   1. GET /bonsai/filing/matter-folder?matter_reference=…&subfolder=07 Costs
    //      resolves drive_id + folder_id for the matter (and the "07 Costs"
    //      subfolder by default for invoice PDFs).
    //   2. POST /bonsai/filing/upload with multipart `file` (re-materialised
    //      from the stored data-URL), `drive_id`, `folder_id`, `filename`,
    //      `matter_reference`.
    //
    // Trust-context: never raise [object Object] / pydantic-detail arrays
    // at the toast layer — safeErrorMessage() coerces every shape to a
    // plain string (preserves the Phase A defensive coercion that closed
    // React #31 / ErrorBoundary crash).
    const ref = (spMatterPick.matter_reference || "").trim();
    if (!ref) {
      toast.error("Pick a matter from the search bar first");
      return;
    }
    const dataUrl = typeof fileData === "string" && fileData ? fileData : finalInvoiceDataUrl;
    if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
      toast.error("Finalised invoice PDF not available — please re-upload before filing");
      return;
    }
    setFilingToSp(true);
    try {
      // Step 1: resolve matter folder + the picker-selected subfolder.
      // Brief 32 — was hardcoded subfolder="07 Costs" pre-fix. Picker
      // value drives this; empty string means user picked Matter root.
      let folderInfo;
      try {
        const folderResp = await axios.get(`${API}/bonsai/filing/matter-folder`, {
          params: {
            matter_reference: ref,
            ...(spSelectedSubfolder ? { subfolder: spSelectedSubfolder } : {}),
          },
          withCredentials: true,
        });
        folderInfo = folderResp.data || {};
      } catch (folderErr) {
        const status = folderErr?.response?.status;
        if (status === 404) {
          toast.error(`SharePoint folder not found for matter ${ref}`);
        } else if (status === 503) {
          toast.error("SharePoint is not configured on the server — contact admin");
        } else {
          toast.error(safeErrorMessage(folderErr, `Folder lookup failed for matter ${ref}`));
        }
        return;
      }

      // Brief 32 — HARD FAIL on subfolder resolution miss. If the picker
      // listed Subfolder X (we fetched it from Graph) but the upload-side
      // resolver missed it, do NOT silently file to matter root. Surface
      // the miss, log a beacon, abort. Silent matter-root fallback was
      // the original bug we are fixing.
      if (spSelectedSubfolder && folderInfo.subfolder_resolved === false) {
        toast.error(
          `Subfolder "${spSelectedSubfolder}" could not be resolved for filing on ${ref}. Please re-run conflict check on this matter and try again.`,
          { duration: 10000 }
        );
        // eslint-disable-next-line no-console
        console.warn(
          `[INVOICE_SP_SUBFOLDER_MISS] matter=${ref} subfolder=${spSelectedSubfolder} picker_listed=true upload_resolved=false`
        );
        return;
      }

      const driveId = folderInfo.drive_id;
      const folderId = folderInfo.folder_id;
      const folderPath = folderInfo.folder_path || folderInfo.folder_name || ref;
      if (!driveId || !folderId) {
        toast.error(`SharePoint folder lookup returned no folder for ${ref}`);
        return;
      }

      // Step 2: re-materialise the PDF blob from the stored data-URL and
      // post it via multipart. fetch(data-URL).blob() is the idiomatic
      // browser primitive here (jsdom mocks supported in tests).
      const blob = await fetch(dataUrl).then((r) => r.blob());
      const formData = new FormData();
      formData.append("file", blob, filename || finalInvoiceFilename || "invoice.pdf");
      formData.append("drive_id", driveId);
      formData.append("folder_id", folderId);
      formData.append("filename", filename || finalInvoiceFilename || "invoice.pdf");
      formData.append("matter_reference", ref);
      formData.append("source", "invoice_tracker_phase_b");

      const resp = await axios.post(`${API}/bonsai/filing/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        withCredentials: true,
      });
      if (resp.data && resp.data.success) {
        toast.success(`Filed to SharePoint: ${folderPath}`);
        setShowSpFiling(false);
        setSpMatterPick({ matter_reference: "", matter_title: "", client_name: "" });
      } else {
        toast.error(safeErrorMessage({ response: resp }, "Filing succeeded but server returned no confirmation"));
      }
    } catch (err) {
      toast.error(safeErrorMessage(err, "Failed to file to SharePoint"));
    } finally {
      setFilingToSp(false);
    }
  }, [spMatterPick, spSelectedSubfolder, finalInvoiceDataUrl, finalInvoiceFilename]);

  // Mark-as-sent — manual status flip after May sends via Outlook.
  // No silent fallback: PATCH must land before we flip local state.
  const handleMarkSentToClient = useCallback(async () => {
    if (sentToClientAt) {
      toast.info("Already marked as sent to client");
      return;
    }
    const nowIso = new Date().toISOString();
    try {
      const result = await updateTracker(
        {
          sent_to_client_at: nowIso,
          sent_to_client_via: "outlook_manual",
          sent_to_client_by: currentUserName || currentUserEmail || "",
        },
        { throwOnError: true }
      );
      if (result) {
        setSentToClientAt(result.sent_to_client_at || nowIso);
        toast.success("Marked as sent to client");
        setShowEmailModal(false);
      }
    } catch (err) {
      toast.error(safeErrorMessage(err, "Could not mark as sent — please retry"));
    }
  }, [updateTracker, sentToClientAt, currentUserName, currentUserEmail]);

  useEffect(() => {
    return () => { if (scratchpadTimerRef.current) clearTimeout(scratchpadTimerRef.current); };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };

  const accentBorder = amberAccent ? "border-amber-500/40" : "border-teal/20";
  const accentLabel = amberAccent ? "text-status-amber" : "text-teal";
  const accentBg = amberAccent ? "from-amber-950/30 to-navy-card" : "from-[color:var(--surface-inset)] to-[color:var(--surface-inset)]";

  return (
    <div className={`md:col-span-12 bg-white rounded-md border ${accentBorder} shadow-sm overflow-hidden`} data-testid="section-invoice-tracker">
      <div className={`px-5 py-4 border-b border-white/10 bg-gradient-to-r ${accentBg}`}>
        <h2 className={`font-semibold ${accentLabel} flex items-center gap-2 text-sm font-sans`}>
          <Receipt className="w-4 h-4" /> Invoice Tracker
        </h2>
      </div>

      <div className="p-5 space-y-6">
        <CloseFileNudge
          invoiceType={subData.invoice_type || submission?.invoice_type}
          matterReference={matterReference}
          clientName={clientName}
        />
        {/* Status Timeline */}
        <div className="flex flex-wrap gap-6">
          <TimelineStep label="Draft Uploaded" done={draftUploaded} />
          <TimelineStep label="Sent to Fee Earner" done={!!notifiedAt} active={draftUploaded && !notifiedAt} />
          <TimelineStep
            label={approvalStatus === "returned" ? "Returned for Revision" : "Approved"}
            done={approvalStatus === "approved"}
            active={approvalStatus === "returned"}
          />
          <TimelineStep label="Sent to Client" done={!!finalInvoiceFilename} active={approvalStatus === "approved" && !finalInvoiceFilename} />
        </div>

        {/* Brief 31 — Cancelled banner. Renders at the top of the panel
            when the row has been cancelled; suppresses Approve / Return /
            Cancel / Save-to-SharePoint / Finalised below (each block is
            gated by !isCancelled). Carries who, when, why, and the best-
            effort link to the superseding billed invoice if one exists on
            the same matter. */}
        {isCancelled && (
          <div
            className="space-y-2 rounded-md border border-slate-300 bg-slate-50 p-4"
            data-testid="invoice-detail-cancelled-banner"
          >
            <div className="text-sm font-semibold text-slate-700">
              This draft was CANCELLED on {fmtDate(cancelledAt)}
              {cancelledBy ? ` by ${cancelledBy}` : ""}.
            </div>
            <div className="text-sm text-slate-600">
              Reason: {cancellationReason || "—"}
            </div>
            <div
              className="text-sm text-slate-600"
              data-testid="invoice-detail-cancelled-supersede"
            >
              {supersedingInvoice
                ? `Superseded by invoice raised ${fmtDate(supersedingInvoice.approved_at)}.`
                : "No superseding invoice on record."}
            </div>
          </div>
        )}

        {/* Draft Upload Section */}
        <div className="space-y-3">
          <h3 className={`text-xs font-medium uppercase tracking-wider ${amberAccent ? "text-status-amber" : "text-slate-400"}`}>Draft Upload</h3>
          {draftUploaded ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-teal-dim rounded-md border border-teal">
                <CheckCircle2 className="w-4 h-4 text-teal shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-teal block truncate">{draftFilename}</span>
                  <span className="text-xs text-teal">Uploaded {fmtDate(draftUploadedAt)}</span>
                </div>
                {/* BM-H4554: a generated draft is updated in place (same
                    record on Finance > Invoices); only a hand-uploaded file
                    is replaced. */}
                <Button size="sm" variant="outline" onClick={() => { setDraftUploaded(false); setDraftFilename(""); setDraftFileData(""); }}
                  className="text-xs h-7 shrink-0" data-testid="draft-replace-button">{nativeInvoiceId ? "Update" : "Replace"}</Button>
              </div>
              <DraftPreview
                filename={draftFilename}
                base64Data={draftFileData}
                onDownload={draftFileData ? handleDownloadDraft : null}
              />
            </div>
          ) : (
            <div className="space-y-3">
              {isMay && matterReference && (
                <NativeInvoiceDraft
                  submissionId={submissionId}
                  stage="draft"
                  existingInvoiceId={nativeInvoiceId}
                  onReady={handleNativeDraftReady}
                />
              )}
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="flex-1 border-t border-white/10" /> or upload manually <div className="flex-1 border-t border-white/10" />
              </div>
              <div
                className={`border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${dragOver ? "border-teal bg-teal-dim" : "border-white/10 hover:border-teal"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-6 h-6 text-txt-muted mx-auto mb-2" />
                <p className="text-sm text-slate-400">{uploading ? "Uploading..." : "Drop PDF or DOCX here, or click to select"}</p>
                <input ref={fileInputRef} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0])} />
              </div>
            </div>
          )}
        </div>

        {/* Notify Fee Earner */}
        <div className="flex flex-wrap items-center gap-3">
          {/* feat/invoice-tracker-button-state (20 May 2026):
              Once notified, the primary Notify button locks. A small
              secondary "resend" link sits nearby for the edge case
              where the FE missed the email. Avoids accidental dupes
              while still leaving an escape hatch. */}
          <Button
            size="sm"
            disabled={!draftUploaded || notifying || sendLocked}
            onClick={handleNotifyFeeEarner}
            className={
              sendLocked
                ? "bg-slate-700 text-slate-300 gap-1.5 cursor-default"
                : "bg-teal hover:bg-teal-hover text-white gap-1.5"
            }
            data-testid="invoice-notify-fee-earner-btn"
            title={sendLocked ? "Fee earner already notified — use Resend if needed" : "Email the fee earner to approve this draft"}
          >
            {sendLocked
              ? <><CheckCircle2 className="w-3.5 h-3.5" /> Notified</>
              : <><Send className="w-3.5 h-3.5" /> {notifying ? "Sending..." : isReturned ? "Send revised draft" : "Send to fee earner"}</>
            }
          </Button>
          {sendLocked && (
            <button
              type="button"
              onClick={handleNotifyFeeEarner}
              disabled={notifying}
              className="text-xs text-teal hover:underline disabled:opacity-50"
              data-testid="invoice-resend-fee-earner-link"
            >
              {notifying ? "Resending..." : "resend"}
            </button>
          )}
          {/* feat/allocate-fe-on-invoice-request (19 May 2026):
              Inline Allocate button so May / a director can fix a
              missing fee earner WITHOUT leaving this screen. Visible
              to the same role set the backend endpoint enforces
              (directors + cashier).
              feat/invoice-tracker-button-state (20 May 2026):
              Locks once a fee earner is already allocated. We show
              the FE name alongside so May can see at a glance who's
              on the matter without scrolling the form. To swap, use
              the matter intake screen — reallocating mid-invoice was
              the rare-edge path that ended up firing the bug in the
              first place. */}
          {isMay && (
            <Button
              size="sm"
              variant="outline"
              disabled={!matterReference || allocFeSubmitting || feeEarnerAllocated}
              onClick={handleOpenAllocateFeeEarner}
              className={
                feeEarnerAllocated
                  ? "border-slate-600 text-slate-400 gap-1.5 cursor-default"
                  : "border-amber-400 text-status-amber hover:bg-status-amber-dim gap-1.5"
              }
              data-testid="invoice-allocate-fee-earner-btn"
              title={
                feeEarnerAllocated
                  ? `Fee earner already allocated: ${allocatedFeeEarner}`
                  : matterReference
                    ? `Allocate / reallocate the fee earner on ${matterReference}`
                    : "Matter reference not resolved on this submission"
              }
            >
              <UserCog className="w-3.5 h-3.5" /> Allocate Fee Earner
            </Button>
          )}
          {isMay && feeEarnerAllocated && (
            <span className="text-xs text-slate-400" data-testid="invoice-allocated-fe-name">
              FE: <span className="text-slate-200">{allocatedFeeEarner}</span>
            </span>
          )}
          {notifiedAt && (
            <span className="text-xs text-slate-400">
              Notified {fmtDate(notifiedAt)}{notifiedBy ? ` by ${notifiedBy}` : ""}
            </span>
          )}
        </div>

        {/* Phase 1 finance — reassign the allocated fee earner. Unlike the
            Allocate button above (which locks once a fee earner exists), this
            control stays live for whoever the backend gate allows: an MD/
            director, the current fee earner, or the original requester. */}
        {canReassignFe && (
          <div
            className="flex flex-wrap items-end gap-2 pt-2"
            data-testid="invoice-reassign-fe-row"
          >
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">
                Reassign Fee Earner
              </label>
              <StaffPicker
                value={reassignFeSelected}
                onChange={setReassignFeSelected}
                placeholder="Select fee earner…"
                disabled={reassigningFe}
                testId="invoice-reassign-fe-picker"
                showInitials
              />
            </div>
            <Button
              size="sm"
              onClick={handleReassignFeeEarner}
              disabled={reassigningFe || !reassignFeSelected}
              className="bg-teal hover:bg-teal-hover text-white gap-1.5"
              data-testid="invoice-reassign-fe-save-btn"
            >
              {reassigningFe ? "Saving…" : "Save"}
            </Button>
          </div>
        )}

        {/* Approval Section — shown when fee earner has been notified */}
        {notifiedAt && !isCancelled && (
          <div className="space-y-3 pt-2 border-t border-white/10">
            <h3 className={`text-xs font-medium uppercase tracking-wider ${amberAccent ? "text-status-amber" : "text-slate-400"}`}>Approval</h3>
            {approvalStatus === "approved" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-teal-dim rounded-md border border-teal">
                  <ThumbsUp className="w-4 h-4 text-teal" />
                  <span className="text-sm font-medium text-teal">Approved{approvedBy ? ` by ${approvedBy}` : ""} {fmtDate(approvedAt)}</span>
                </div>
              </div>
            ) : approvalStatus === "returned" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-status-amber-dim rounded-md border border-amber-200">
                  <RotateCcw className="w-4 h-4 text-status-amber" />
                  <span className="text-sm font-medium text-status-amber">Returned for revision{approvedBy ? ` by ${approvedBy}` : ""} {fmtDate(approvedAt)}</span>
                </div>
                <p className="text-xs text-slate-400" data-testid="invoice-returned-next-step">
                  Update the draft, then press Send revised draft so the fee earner can approve it.
                </p>
                {annotation && (
                  <div className="p-3 bg-status-amber-dim/50 rounded-md border border-amber-100 text-sm text-status-amber">
                    <div className="text-xs font-medium text-status-amber mb-1">Annotations:</div>
                    {annotation}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-start gap-3">
                <Button size="sm" onClick={handleApprove} disabled={approving}
                  className="bg-teal hover:bg-teal text-white gap-1.5">
                  <ThumbsUp className="w-3.5 h-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAnnotation(!showAnnotation)} disabled={approving}
                  className="border-amber-400 text-status-amber hover:bg-status-amber-dim gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> Return for Revision
                </Button>
                {showAnnotation && (
                  <div className="w-full space-y-2 mt-1">
                    <textarea
                      value={annotation}
                      onChange={(e) => setAnnotation(e.target.value)}
                      placeholder="Add annotations or revision notes..."
                      className="w-full p-3 rounded-md border border-amber-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 min-h-[80px] resize-y"
                    />
                    <Button size="sm" onClick={handleReturn} disabled={approving}
                      className="bg-status-amber hover:bg-status-amber text-white">
                      {approving ? "Submitting..." : "Submit Return"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* BM-H4637: Cancel used to sit inside the Approval section, which
            only shows once the fee earner has been notified. Since BM-H4554
            a saved draft no longer notifies them, so a request with no
            draft, an unsent draft or a returned draft had no Cancel at all.
            One button now, on every request that is not cancelled and not
            yet sent to the client. Soft-warn copy for approved rows lives
            in the modal. */}
        {canCancelInvoiceRequest(approvalStatus, sentToClientAt) && (
          <div className="pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCancelModal(true)}
              disabled={approving || cancelling}
              className="border-slate-300 text-slate-600 hover:bg-slate-50 gap-1.5"
              data-testid="invoice-detail-cancel-button"
            >
              Cancel Draft
            </Button>
          </div>
        )}

        {/* Finalised and Sent to Client — May only, after Approved */}
        {/* Brief 31 — V cross-brief clarification 04 Jun 2026: a Cancelled
            row's detail panel suppresses every filing/approval-related
            section (Approve / Return / Cancel / Save-to-SP including
            Brief 32's picker). Only the banner + history remain. */}
        {isMay && approvalStatus === "approved" && !isCancelled && (
          <div className="space-y-3 pt-2 border-t border-white/10">
            <h3 className={`text-xs font-medium uppercase tracking-wider ${amberAccent ? "text-status-amber" : "text-slate-400"}`}>
              Finalised &amp; Sent to Client
            </h3>

            {/* Email Client */}
            {!showEmailModal ? (
              <Button size="sm" onClick={() => setShowEmailModal(true)}
                className="bg-teal hover:bg-teal-hover text-white gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Email Client
              </Button>
            ) : (
              <div className="space-y-3 p-4 rounded-md border border-white/10 bg-[color:var(--surface-inset)]">
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">To</label>
                  <input
                    type="text"
                    readOnly
                    value={invoiceEmail}
                    className="w-full p-2 rounded-md border border-white/10 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Subject</label>
                  <input
                    type="text"
                    readOnly
                    value={`Invoice — ${clientName} — ${matterReference}`}
                    className="w-full p-2 rounded-md border border-white/10 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Message</label>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full p-3 rounded-md border border-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40 min-h-[80px] resize-y"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={handleOpenEmailClient}
                    className="bg-teal hover:bg-teal-hover text-white gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Open in Outlook
                  </Button>
                  {/* Mark as sent (23 May 2026): closes the loop after Outlook
                      hand-off so the tracker status flips to "Sent to Client".
                      Hidden once flipped — the timestamp badge below replaces it. */}
                  {!sentToClientAt && (
                    <Button size="sm" variant="outline" onClick={handleMarkSentToClient}
                      className="gap-1.5" data-testid="mark-sent-to-client">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark as sent to client
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
                </div>
                {sentToClientAt ? (
                  <p className="text-xs text-teal" data-testid="sent-to-client-badge">
                    Sent to client {fmtDate(sentToClientAt)}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    Opens your default email client with the message pre-filled. After sending, click “Mark as sent to client” to update the tracker.
                  </p>
                )}
              </div>
            )}

            {/* File to SharePoint after sending */}
            {showEmailModal && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowSpFiling(!showSpFiling)}
                  className="text-xs text-teal hover:underline flex items-center gap-1"
                >
                  <FolderUp className="w-3 h-3" /> File to SharePoint
                </button>
                {showSpFiling && (
                  <div className="space-y-2">
                    {/* Brief 9 Phase B — SmartClientMatterSearch replaces dumb input */}
                    <SmartClientMatterSearch
                      label=""
                      placeholder="Search matters by ref, client, or description..."
                      mattersOnly
                      onSelect={(r) => setSpMatterPick({
                        matter_reference: r?.matter_reference || "",
                        matter_title: r?.matter_title || r?.label || "",
                        client_name: r?.client_name || "",
                      })}
                      darkMode={false}
                      data-testid="sp-matter-search-invoice-email"
                    />
                    {spMatterPick.matter_reference && (
                      <p className="text-xs text-slate-500" data-testid="sp-matter-context-invoice-email">
                        Save to: <span className="font-medium text-teal">{spMatterPick.matter_reference}</span>
                        {spMatterPick.client_name ? ` — ${spMatterPick.client_name}` : ""}
                      </p>
                    )}
                    {/* Brief 32 — subfolder picker. Renders inline below
                        the matter pick once a matter has been chosen.
                        Populated from /matter-subfolders; Financials is
                        pre-selected when the backend returns it. */}
                    {spMatterPick.matter_reference && (
                      <div className="space-y-1">
                        <label
                          htmlFor="sp-subfolder-picker-invoice-email"
                          className="block text-xs text-slate-500"
                        >
                          File into subfolder
                        </label>
                        {spSubfolderLoading && (
                          <p
                            className="text-xs text-slate-500"
                            data-testid="invoice-detail-sp-subfolder-loading-invoice-email"
                          >
                            Loading subfolders…
                          </p>
                        )}
                        {spSubfolderError && (
                          <p
                            className="text-xs text-status-red"
                            role="alert"
                            data-testid="invoice-detail-sp-subfolder-error-invoice-email"
                          >
                            {spSubfolderError}
                          </p>
                        )}
                        {!spSubfolderLoading
                          && !spSubfolderError
                          && spSubfolders.length === 0
                          && (
                            <p
                              className="text-xs text-status-amber"
                              data-testid="invoice-detail-sp-subfolder-empty-invoice-email"
                            >
                              No subfolders found — will file to matter root.
                            </p>
                          )}
                        {!spSubfolderLoading && spSubfolders.length > 0 && (
                          <select
                            id="sp-subfolder-picker-invoice-email"
                            value={spSelectedSubfolder}
                            onChange={(e) => setSpSelectedSubfolder(e.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
                            data-testid="invoice-detail-sp-subfolder-picker-invoice-email"
                          >
                            {!spDefaultSubfolder && (
                              <option value="">— Matter root —</option>
                            )}
                            {spSubfolders.map((s) => (
                              <option key={s.id} value={s.name}>
                                {s.name}
                                {s.name === spDefaultSubfolder ? " (default)" : ""}
                              </option>
                            ))}
                            {spDefaultSubfolder && (
                              <option value="">— Matter root (no subfolder) —</option>
                            )}
                          </select>
                        )}
                      </div>
                    )}
                    <Button
                      size="sm"
                      disabled={filingToSp || !spMatterPick.matter_reference}
                      onClick={() => handleFileToSharePoint(null, "invoice-email")}
                      className="bg-teal hover:bg-teal-hover text-white text-xs h-8"
                      data-testid="sp-save-btn-invoice-email"
                    >
                      {filingToSp ? "Filing..." : "Save to SP"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload Finalised Invoice — May only, after Approved */}
        {isMay && approvalStatus === "approved" && !isCancelled && (
          <div className="space-y-3 pt-2 border-t border-white/10">
            <h3 className={`text-xs font-medium uppercase tracking-wider ${amberAccent ? "text-status-amber" : "text-slate-400"}`}>
              Finalise invoice
            </h3>
            {finalInvoiceFilename ? (
              <div className="flex items-center gap-3 p-3 bg-teal-dim rounded-md border border-teal">
                <CheckCircle2 className="w-4 h-4 text-teal shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-teal block truncate">{finalInvoiceFilename}</span>
                  <span className="text-xs text-teal">Uploaded {fmtDate(finalInvoiceUploadedAt)}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setFinalInvoiceFilename(""); setFinalInvoiceUploadedAt(""); setFinalInvoiceDataUrl(""); }}
                  className="text-xs h-7 shrink-0">Replace</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {nativeInvoiceId && (
                  <NativeInvoiceDraft
                    submissionId={submissionId}
                    stage="issue"
                    existingInvoiceId={nativeInvoiceId}
                    onReady={handleNativeFinalReady}
                  />
                )}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="flex-1 border-t border-white/10" /> or upload manually <div className="flex-1 border-t border-white/10" />
                </div>
                <div
                  className="border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer border-white/10 hover:border-teal"
                  onClick={() => finalFileInputRef.current?.click()}
                >
                  <Upload className="w-6 h-6 text-txt-muted mx-auto mb-2" />
                  <p className="text-sm text-slate-400">{uploadingFinal ? "Uploading..." : "Drop finalised invoice PDF here, or click to select"}</p>
                  <input ref={finalFileInputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => handleFinalInvoiceUpload(e.target.files[0])} />
                </div>
              </div>
            )}

            {/* File finalised invoice to SharePoint */}
            {finalInvoiceFilename && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowSpFiling(!showSpFiling)}
                  className="text-xs text-teal hover:underline flex items-center gap-1"
                >
                  <FolderUp className="w-3 h-3" /> Save to SharePoint
                </button>
                {showSpFiling && (
                  <div className="space-y-2">
                    {/* Brief 9 Phase B — SmartClientMatterSearch replaces dumb input */}
                    <SmartClientMatterSearch
                      label=""
                      placeholder="Search matters by ref, client, or description..."
                      mattersOnly
                      onSelect={(r) => setSpMatterPick({
                        matter_reference: r?.matter_reference || "",
                        matter_title: r?.matter_title || r?.label || "",
                        client_name: r?.client_name || "",
                      })}
                      darkMode={false}
                      data-testid="sp-matter-search-finalised"
                    />
                    {spMatterPick.matter_reference && (
                      <p className="text-xs text-slate-500" data-testid="sp-matter-context-finalised">
                        Save to: <span className="font-medium text-teal">{spMatterPick.matter_reference}</span>
                        {spMatterPick.client_name ? ` — ${spMatterPick.client_name}` : ""}
                      </p>
                    )}
                    {/* Brief 32 — subfolder picker (finalised invoice path). */}
                    {spMatterPick.matter_reference && (
                      <div className="space-y-1">
                        <label
                          htmlFor="sp-subfolder-picker-finalised"
                          className="block text-xs text-slate-500"
                        >
                          File into subfolder
                        </label>
                        {spSubfolderLoading && (
                          <p
                            className="text-xs text-slate-500"
                            data-testid="invoice-detail-sp-subfolder-loading-finalised"
                          >
                            Loading subfolders…
                          </p>
                        )}
                        {spSubfolderError && (
                          <p
                            className="text-xs text-status-red"
                            role="alert"
                            data-testid="invoice-detail-sp-subfolder-error-finalised"
                          >
                            {spSubfolderError}
                          </p>
                        )}
                        {!spSubfolderLoading
                          && !spSubfolderError
                          && spSubfolders.length === 0
                          && (
                            <p
                              className="text-xs text-status-amber"
                              data-testid="invoice-detail-sp-subfolder-empty-finalised"
                            >
                              No subfolders found — will file to matter root.
                            </p>
                          )}
                        {!spSubfolderLoading && spSubfolders.length > 0 && (
                          <select
                            id="sp-subfolder-picker-finalised"
                            value={spSelectedSubfolder}
                            onChange={(e) => setSpSelectedSubfolder(e.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
                            data-testid="invoice-detail-sp-subfolder-picker-finalised"
                          >
                            {!spDefaultSubfolder && (
                              <option value="">— Matter root —</option>
                            )}
                            {spSubfolders.map((s) => (
                              <option key={s.id} value={s.name}>
                                {s.name}
                                {s.name === spDefaultSubfolder ? " (default)" : ""}
                              </option>
                            ))}
                            {spDefaultSubfolder && (
                              <option value="">— Matter root (no subfolder) —</option>
                            )}
                          </select>
                        )}
                      </div>
                    )}
                    <Button
                      size="sm"
                      disabled={filingToSp || !spMatterPick.matter_reference}
                      onClick={() => handleFileToSharePoint(finalInvoiceDataUrl, finalInvoiceFilename)}
                      className="bg-teal hover:bg-teal-hover text-white text-xs h-8"
                      data-testid="sp-save-btn-finalised"
                    >
                      {filingToSp ? "Filing..." : "Save to SP"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Scratchpad */}
        <div className="space-y-2 pt-2 border-t border-white/10">
          <h3 className={`text-xs font-medium uppercase tracking-wider ${amberAccent ? "text-status-amber" : "text-slate-400"} flex items-center gap-1.5`}>
            <MessageSquare className="w-3.5 h-3.5" /> Scratchpad
          </h3>
          <textarea
            value={scratchpad}
            onChange={(e) => setScratchpad(e.target.value)}
            onBlur={handleScratchpadBlur}
            placeholder="Add notes..."
            className="w-full p-3 rounded-md border border-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40 min-h-[60px] resize-y"
          />
          {scratchpadMeta.at && (
            <p className="text-xs text-txt-muted">
              Last edited{scratchpadMeta.by ? ` by ${scratchpadMeta.by}` : ""} at {fmtDate(scratchpadMeta.at)}
            </p>
          )}
        </div>
      </div>

      {/* Brief 31 — Cancel Draft confirmation dialog. Mandatory reason
          (50-1000 char on the FE; server enforces non-empty + <=1000).
          Soft-warn copy on approved rows nudges the user to consider a
          credit note instead. Buttons gated on busy + minimum-length so
          accidental empty submits never round-trip. */}
      <Dialog open={showCancelModal} onOpenChange={(open) => { if (!open) { setShowCancelModal(false); setCancelReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="invoice-detail-cancel-modal">
          <DialogHeader>
            <DialogTitle>Cancel Draft</DialogTitle>
            <DialogDescription>
              Withdraw this invoice draft. The audit row records who, when,
              and why. Re-opening requires raising a fresh draft.
            </DialogDescription>
          </DialogHeader>
          {approvalStatus === "approved" && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
              data-testid="invoice-detail-cancel-warning"
            >
              This invoice has already been approved. Consider raising a
              credit note instead of cancelling. Continue only if the
              approval was issued in error.
            </div>
          )}
          <div className="space-y-2">
            <label
              htmlFor="invoice-detail-cancel-reason-input"
              className="block text-xs font-medium text-gray-700"
            >
              Why is this draft being cancelled? (e.g. client billed via
              another invoice, matter closed, duplicate request)
            </label>
            <textarea
              id="invoice-detail-cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="Reason (required, up to 1000 characters)"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
              data-testid="invoice-detail-cancel-reason"
            />
            <p className="text-xs text-gray-500">
              {cancelReason.trim().length} / 1000 characters. Reason is required and written to the audit row.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowCancelModal(false); setCancelReason(""); }}
              disabled={cancelling}
              data-testid="invoice-detail-cancel-keep"
            >
              Keep Draft
            </Button>
            <Button
              onClick={handleSubmitCancel}
              disabled={cancelling || cancelReason.trim().length === 0 || cancelReason.trim().length > 1000}
              title={
                cancelReason.trim().length === 0
                  ? "Type a reason above to enable this button"
                  : cancelReason.trim().length > 1000
                  ? "Reason is over the 1000-character limit"
                  : ""
              }
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="invoice-detail-cancel-confirm"
            >
              {cancelling ? "Cancelling..." : "Cancel Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* feat/allocate-fe-on-invoice-request — Allocate dialog. Mirrors
          MatterIntakeDetail's dialog so the two code paths stay in
          lock-step (same endpoint, same validation, same UX). */}
      <Dialog open={allocFeOpen} onOpenChange={setAllocFeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate Fee Earner</DialogTitle>
            <DialogDescription>
              Sets the responsible fee earner on matter{" "}
              <span className="font-mono">{matterReference || "(unknown)"}</span>.
              Overrides any value from the Pre-CaseMinder Q6 fallback.
              Restricted to directors and the cashier (May).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Fee earner
            </label>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              value={allocFeSelected}
              onChange={(e) => setAllocFeSelected(e.target.value)}
              disabled={allocFeLoading || allocFeSubmitting}
              data-testid="invoice-allocate-fee-earner-select"
            >
              <option value="">
                {allocFeLoading ? "Loading…" : "— Select a fee earner —"}
              </option>
              {allocFeOptions.map((fe) => (
                <option key={fe.email || fe.name} value={fe.name}>
                  {fe.name}
                  {fe.email ? ` (${fe.email})` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              The list comes from staff_config (active fee earners only).
              If a name is missing here, add it to staff_config first.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAllocFeOpen(false)}
              disabled={allocFeSubmitting}
              data-testid="invoice-allocate-fee-earner-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAllocateFeeEarner}
              disabled={!allocFeSelected || allocFeSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="invoice-allocate-fee-earner-submit"
            >
              {allocFeSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Allocating…
                </>
              ) : (
                "Allocate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// BM-H641 (29 Sep 2026), Vanessa: "would be good to nudge fee earners to close
// files when they request a final invoice". Shown on a Final invoice request
// until the matter is closed (GET /matters/{ref}/closed, the hub.matter_status
// rule). The closure form opens with the matter already filled in.
// BM-H4637: a request can be cancelled at any point before it goes to the
// client, whether or not a draft exists or the fee earner has seen it.
export function canCancelInvoiceRequest(approvalStatus, sentToClientAt) {
  return approvalStatus !== "cancelled" && !sentToClientAt;
}

export function CloseFileNudge({ invoiceType, matterReference, clientName }) {
  const [closed, setClosed] = useState(null);
  const isFinal = (invoiceType || "").trim().toLowerCase() === "final";
  useEffect(() => {
    if (!isFinal || !matterReference) return;
    axios.get(`${API}/matters/${encodeURIComponent(matterReference)}/closed`)
      .then((r) => setClosed(!!r.data?.closed))
      .catch(() => setClosed(null));
  }, [isFinal, matterReference]);
  if (!isFinal || !matterReference || closed !== false) return null;
  const q = new URLSearchParams({ matter_reference: matterReference, client_name: clientName || "" });
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-md border border-amber-500/40 bg-amber-50 text-sm"
      data-testid="close-file-nudge">
      <span className="text-amber-900">This is the final invoice. Once it is paid, close the file.</span>
      <a href={`/file-closure?${q.toString()}`} className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium"
        data-testid="close-file-nudge-link">Close the file</a>
    </div>
  );
}

export default function InvoiceRequestDetail({ submission, files, formatDate, handleDownloadPDF, handleDownloadFile, handleExportJson, downloadingPdf, navigate, isDirector, onDelete }) {
  // Resolve a field value by checking every location where it might live:
  //   1. submission.data.<key>           – normal flat form storage
  //   2. submission.<key>                – root-level (some docs store fields here)
  //   3. submission.data.<section>.<key> – nested export-json shape inside data
  //   4. submission.<section>.<key>      – nested export-json shape at root
  //   5. submission.primary_label / secondary_label – listing-endpoint aliases
  const raw  = submission?.data || {};
  const root = submission || {};
  // raw_form_data exists in the export-json format as a flat copy of all fields
  const rfd  = raw.raw_form_data || root.raw_form_data || {};
  const pick = (...paths) => {
    for (const p of paths) {
      if (p != null && p !== "") return p;
    }
    return "";
  };

  const inv  = raw.invoice_info    || root.invoice_info    || {};
  const cc   = raw.client_contact  || root.client_contact  || {};
  const it   = raw.invoice_type    || root.invoice_type    || {};
  const dis  = raw.disbursements   || root.disbursements   || {};
  const moa  = raw.moa_third_party || root.moa_third_party || {};
  const del_ = raw.delivery        || root.delivery        || {};
  const appr = raw.approval        || root.approval        || {};

  // invoice_type can be a string ("Interim") or a section object — normalise
  const itSection = typeof it === "object" ? it : {};

  const data = {
    ...rfd,
    ...raw,
    invoice_request_number:  pick(raw.invoice_request_number,  root.invoice_request_number,  inv.invoice_request_number,  rfd.invoice_request_number),
    requester_name:          pick(raw.requester_name,          root.requester_name,          inv.requester_name,          rfd.requester_name),
    requester_email:         pick(raw.requester_email,         root.requester_email,         inv.requester_email,         rfd.requester_email),
    date:                    pick(raw.date,                    root.date,                    inv.date,                    rfd.date),
    client_name:             pick(raw.client_name,             root.client_name,             root.primary_label,  inv.client_name, rfd.client_name),
    matter_title:            pick(raw.matter_title,            root.matter_title,            inv.matter_title,            rfd.matter_title),
    matter_reference:        pick(raw.matter_reference,        root.matter_reference,        root.secondary_label, inv.matter_reference, rfd.matter_reference),
    client_invoice_email:    pick(raw.client_invoice_email,    root.client_invoice_email,    cc.client_invoice_email,    rfd.client_invoice_email),
    invoice_comments:        pick(raw.invoice_comments,        root.invoice_comments,        cc.invoice_comments,        rfd.invoice_comments),
    invoice_type:            pick(typeof raw.invoice_type === "string" ? raw.invoice_type : null, typeof root.invoice_type === "string" ? root.invoice_type : null, itSection.invoice_type, rfd.invoice_type),
    wip_or_fixed:            pick(raw.wip_or_fixed,            root.wip_or_fixed,            itSection.wip_or_fixed,     rfd.wip_or_fixed),
    wip_time_checked:        pick(raw.wip_time_checked,        root.wip_time_checked,        itSection.wip_time_checked, rfd.wip_time_checked),
    // Brief 33 — hydrate fixed-fee amount + narrative from any of the
    // structured shapes the legacy submission payload can land in.
    fixed_fee_amount:        pick(raw.fixed_fee_amount,        root.fixed_fee_amount,        itSection.fixed_fee_amount, rfd.fixed_fee_amount),
    fixed_fee_narrative:     pick(raw.fixed_fee_narrative,     root.fixed_fee_narrative,     itSection.fixed_fee_narrative, rfd.fixed_fee_narrative),
    notification_preference: pick(raw.notification_preference, root.notification_preference, del_.notification_preference, appr.notification_preference, rfd.notification_preference),
    draft_required:          pick(raw.draft_required,          root.draft_required,          del_.draft_required,         rfd.draft_required),
    further_info:            pick(raw.further_info,            root.further_info,            del_.further_info,           rfd.further_info),
    disbursements_noted:     pick(raw.disbursements_noted,     root.disbursements_noted,     dis.disbursements_noted,     rfd.disbursements_noted),
    accounts_add_disbursements: pick(raw.accounts_add_disbursements, root.accounts_add_disbursements, dis.accounts_add_disbursements, rfd.accounts_add_disbursements),
    tt_fee_count:            pick(raw.tt_fee_count,            root.tt_fee_count,            dis.tt_fee_count,            rfd.tt_fee_count),
    ob_fee_count:            pick(raw.ob_fee_count,            root.ob_fee_count,            dis.ob_fee_count,            rfd.ob_fee_count),
    ...Object.fromEntries(INVOICE_REQUEST_FEES.map(f => [f.field, pick(raw[f.field], root[f.field], dis[f.field], rfd[f.field])])),
    other_disbursement:      pick(raw.other_disbursement,      root.other_disbursement,      dis.other_disbursement,      rfd.other_disbursement),
    hold_moa:                pick(raw.hold_moa,                root.hold_moa,                moa.hold_moa,                rfd.hold_moa),
    use_moa:                 pick(raw.use_moa,                 root.use_moa,                 moa.use_moa,                 rfd.use_moa),
    moa_retention_checked:   pick(raw.moa_retention_checked,   root.moa_retention_checked,   moa.moa_retention_checked,   rfd.moa_retention_checked),
    third_party_invoice:     pick(raw.third_party_invoice,     root.third_party_invoice,     moa.third_party_invoice,     rfd.third_party_invoice),
    cto_notification:        pick(raw.cto_notification,        root.cto_notification,        moa.cto_notification,        rfd.cto_notification),
  };
  const thirdPartyFile = files.find(f => f.file_type === "third_party_invoice");

  // time-recording-foundations (ticket 3): the LawBillity expense rows the
  // fee earner selected on the Disbursements step used to be frozen into
  // data.lawbillity_expenses and never shown again on this review page.
  // They're now recorded natively (source_ref = this submission's id) on
  // submit -- fetch and show them here instead of the dead field.
  const [nativeDisbursements, setNativeDisbursements] = useState([]);
  const [nativeDisbursementsLoading, setNativeDisbursementsLoading] = useState(false);
  useEffect(() => {
    const subId = submission?.id;
    if (!subId) return;
    let cancelled = false;
    setNativeDisbursementsLoading(true);
    axios.get(`${API}/disbursements`, { params: { source_ref: subId } })
      .then((res) => {
        if (!cancelled) setNativeDisbursements(res.data?.disbursements || []);
      })
      .catch(() => {
        if (!cancelled) setNativeDisbursements([]);
      })
      .finally(() => {
        if (!cancelled) setNativeDisbursementsLoading(false);
      });
    return () => { cancelled = true; };
  }, [submission?.id]);
  const nativeDisbursementsTotal = nativeDisbursements.reduce(
    (sum, d) => sum + (parseFloat(d.amount) || 0), 0,
  );

  const alerts = [];
  if (data.use_moa === "Yes") alerts.push({ color: "yellow", icon: AlertCircle, text: "MOA to be used against this invoice" });
  if (data.accounts_add_disbursements === "Yes") alerts.push({ color: "blue", icon: Receipt, text: "Disbursements to be added by Accounts" });
  if (data.third_party_invoice === "Yes") alerts.push({ color: "purple", icon: FileCheck, text: `Third party invoice ${thirdPartyFile ? "attached" : "required"}${data.cto_notification === "Yes" ? " (CTO notification required)" : ""}` });
  if (data.draft_required === "Yes") alerts.push({ color: "slate", icon: FileText, text: "Draft required before sending to client" });

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={data.matter_reference || "Invoice Request"}
      subtitle={data.client_name}
      matterReference={data.matter_reference}
      badge="Invoice Request"
      badgeColor="blue"
      badgeIcon={Receipt}
      isTest={data.is_test_submission}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={<DownloadActions handleDownloadPDF={handleDownloadPDF} handleExportJson={handleExportJson} downloadingPdf={downloadingPdf} />}
    >
      {/* Status Row */}
      <div className="flex items-center justify-between bg-white rounded-md border border-teal/20 px-5 py-3.5 shadow-sm" data-testid="invoice-header-info">
        <div className="flex items-center gap-4">
          <StatusBadge status={submission.status} />
          {data.invoice_request_number && (
            <span
              className="text-xs font-mono font-semibold tracking-wide text-teal bg-teal/10 border border-teal/30 rounded px-2 py-0.5"
              data-testid="invoice-request-number"
            >
              {data.invoice_request_number}
            </span>
          )}
          <span className="text-sm text-slate-400">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Alert Banners */}
      {alerts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="invoice-alerts">
          {alerts.map((a, i) => (
            <AlertBanner key={i} icon={a.icon} color={a.color}>{a.text}</AlertBanner>
          ))}
        </div>
      )}

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="invoice-bento-grid">

        {/* Hero: Invoice Information — spans 8 cols */}
        <div className="md:col-span-8 bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-invoice-information">
          <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <User className="w-4 h-4" /> Invoice Information
            </h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <HeroField icon={User} label="Requester" value={data.requester_name} large />
            <HeroField icon={Mail} label="Email" value={data.requester_email} />
            <HeroField icon={Building2} label="Client" value={data.client_name} large />
            <HeroField icon={CalendarDays} label="Date" value={data.date} />
            <HeroField icon={FileText} label="Matter Title" value={data.matter_title} />
            <HeroField icon={Hash} label="Matter Reference" value={data.matter_reference} large />
          </div>
        </div>

        {/* Status Card — spans 4 cols */}
        <div className="md:col-span-4 space-y-4">
          {/* Invoice Type Card */}
          <div className="bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-invoice-type">
            <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <FileText className="w-4 h-4" /> Invoice Type & Basis
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Type</span>
                <InvoiceTypeBadge type={data.invoice_type} />
              </div>
              <FinancialRow label="WIP or Fixed Fee" value={data.wip_or_fixed} />
              {data.wip_or_fixed === "WIP" && (
                <FinancialRow label="Time Checked" value={data.wip_time_checked} accent />
              )}
              {/* Brief 33 — fixed-fee amount row + dedicated narrative
                  block. Option (b) chosen by V: dedicated block below
                  the row so newlines survive (whitespace-pre-wrap) and
                  FinancialRow stays untouched. */}
              {data.wip_or_fixed === "Fixed fee" && (
                <FinancialRow label="Fixed Fee Amount" value={data.fixed_fee_amount} accent />
              )}
              {data.wip_or_fixed === "Fixed fee" && data.fixed_fee_narrative && (
                <div
                  className="mt-2 p-3 bg-teal-dim/20 rounded-md"
                  data-testid="fixed-fee-narrative-block"
                >
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
                    Fixed Fee Narrative
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                    {data.fixed_fee_narrative}
                  </div>
                </div>
              )}
              <FinancialRow label="Draft Required" value={data.draft_required} accent={data.draft_required === "Yes"} />
              <FinancialRow label="Notification" value={data.notification_preference} />
            </div>
          </div>

          {/* Money on Account Card */}
          <div className="bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-money-on-account">
            <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Banknote className="w-4 h-4" /> Money on Account
              </h2>
            </div>
            <div className="p-5">
              <FinancialRow label="Hold MOA" value={data.hold_moa} />
              {data.hold_moa === "Yes" && (
                <>
                  <FinancialRow label="Use Against Invoice" value={data.use_moa} accent={data.use_moa === "Yes"} />
                  {data.use_moa === "Yes" && (
                    <FinancialRow label="Retention Checked" value={data.moa_retention_checked} accent />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Client Contact — spans 6 cols */}
        <div className="md:col-span-6 bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-client-contact">
          <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Mail className="w-4 h-4" /> Client Contact
            </h2>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-teal/10 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-teal" />
              </div>
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Invoice Email</span>
                <span className="text-sm font-medium text-white">{data.client_invoice_email || "\u2014"}</span>
              </div>
            </div>
            {data.invoice_comments && (
              <div className="mt-4 p-3.5 bg-[color:var(--surface-inset)] rounded-md text-sm text-slate-300 border border-white/10">
                <div className="font-medium text-white mb-1 text-xs uppercase tracking-wider">Comments</div>
                {data.invoice_comments}
              </div>
            )}
          </div>
        </div>

        {/* Disbursements — spans 6 cols */}
        <div className="md:col-span-6 bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-disbursements">
          <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Receipt className="w-4 h-4" /> Disbursements
            </h2>
          </div>
          <div className="p-5">
            {nativeDisbursementsLoading && (
              <div className="text-xs text-slate-500 mb-2" data-testid="disbursements-loading">Loading disbursements…</div>
            )}
            {!nativeDisbursementsLoading && nativeDisbursements.length > 0 && (
              <div className="mb-3.5 p-3.5 bg-teal/5 rounded-md border border-teal/20 space-y-1.5" data-testid="native-disbursements-list">
                <div className="text-xs font-medium text-teal uppercase tracking-wider mb-1.5">
                  Selected from LawBillity
                </div>
                {nativeDisbursements.map((d) => (
                  <div key={d.id} className="flex justify-between text-sm" data-testid={`native-disbursement-${d.id}`}>
                    <span className="text-slate-700">{d.description}</span>
                    <span className="font-medium text-slate-900">
                      £{parseFloat(d.amount).toFixed(2)}
                      {d.vat_treatment === "plus_vat" && " + VAT"}
                      {d.vat_treatment === "inc_vat" && " (inc VAT)"}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-1.5 border-t border-teal/20">
                  <span className="text-teal font-medium">Total</span>
                  <span className="font-semibold text-teal">£{nativeDisbursementsTotal.toFixed(2)}</span>
                </div>
              </div>
            )}
            <FinancialRow label="All Noted" value={data.disbursements_noted} />
            <FinancialRow label="Accounts to Add" value={data.accounts_add_disbursements} accent={data.accounts_add_disbursements === "Yes"} />
            {data.accounts_add_disbursements === "Yes" && (
              <div className="mt-3 p-3.5 bg-blue-50 rounded-md border border-blue-100 space-y-2">
                {INVOICE_REQUEST_FEES.filter(f => data[f.field] && data[f.field] !== "0").map(fee => (
                  <div key={fee.field} className="flex justify-between text-sm">
                    <span className="text-blue-700">{fee.label}</span>
                    <span className="font-semibold text-blue-900">{data[fee.field]} x {feeRate(fee)}</span>
                  </div>
                ))}
                {/* Submissions made before 29 Sep 2026 carry the old two fields. */}
                {LEGACY_INVOICE_REQUEST_FEES.filter(f => data[f.field] && data[f.field] !== "0").map(fee => (
                  <div key={fee.field} className="flex justify-between text-sm">
                    <span className="text-blue-700">{fee.label}</span>
                    <span className="font-semibold text-blue-900">{data[fee.field]}{fee.rate ? ` x ${fee.rate}` : ""}</span>
                  </div>
                ))}
                {data.other_disbursement && (
                  <div className="text-sm">
                    <span className="text-blue-700">Other: </span>
                    <span className="text-blue-900">{data.other_disbursement}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Third Party Invoice — full width */}
        {data.third_party_invoice === "Yes" && (
          <div className="md:col-span-12 bg-white rounded-md border border-purple-200 shadow-sm overflow-hidden" data-testid="section-third-party">
            <div className="px-5 py-4 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-white">
              <h2 className="font-semibold text-purple-700 flex items-center gap-2 text-sm font-sans">
                <FileCheck className="w-4 h-4" /> Third Party Invoice
              </h2>
            </div>
            <div className="p-5">
              <FinancialRow label="CTO Notification" value={data.cto_notification} accent={data.cto_notification === "Yes"} />
              <div className="mt-3 p-4 bg-purple-50 rounded-md border border-purple-100">
                <div className="text-xs font-medium text-purple-700 uppercase tracking-wider mb-2">Attached File</div>
                {thirdPartyFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-status-green" />
                      <span className="text-sm text-purple-900 font-medium">{thirdPartyFile.filename}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadFile(thirdPartyFile.file_id, thirdPartyFile.filename)}
                      className="text-purple-700 border-purple-300 hover:bg-purple-100 gap-1 text-xs h-7" data-testid="download-third-party-file">
                      <Download className="w-3.5 h-3.5" /> Download
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-status-amber">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">No file uploaded yet</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Additional Information — full width */}
        {data.further_info && (
          <div className="md:col-span-12 bg-white rounded-md border border-teal/20 shadow-sm overflow-hidden" data-testid="section-additional-info">
            <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <FileText className="w-4 h-4" /> Additional Information
              </h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-300 leading-relaxed">{data.further_info}</p>
            </div>
          </div>
        )}
        {/* Invoice Tracker Panel — full width */}
        <InvoiceTrackerPanel submission={submission} />
      </div>
    </SubmissionLayout>
    {data.matter_reference && (
      <BonsaiMate context="matter" matterReference={data.matter_reference} />
    )}
    </>
  );
}
